// Колонка детали — правая часть области раздела. Шапка держит заголовок, действия над
// объектом (правило 3: действие живёт там, где объект) и вкладки; всё это слоты, их
// содержимое кладут модули. Без шапочных слотов колонка отдаёт всю высоту содержимому —
// так живут заглушки и полноэкранные состояния.

import type { ReactNode } from 'react';

import { RiArrowLeftSLine } from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router';

export interface DetailPaneProps {
  /** Заголовок детали: кто или что открыто. */
  title?: ReactNode;
  /** Действия над открытым объектом — в шапке его детали. */
  actions?: ReactNode;
  /** Вкладки карточки (потолок — восемь, ПРАВИЛА.md, правило 12). */
  tabs?: ReactNode;
  children: ReactNode;
}

export function DetailPane({ title, actions, tabs, children }: DetailPaneProps) {
  const hasHeader = title !== undefined || actions !== undefined || tabs !== undefined;
  const navigate = useNavigate();
  const location = useLocation();

  // Возврат к списку — ВИДИМАЯ кнопка, а не жест от края. Формулировка «возврат жестом»
  // стояла в ПРАВИЛАХ до 08.09.2026 и стоила трёх экранов без единого видимого выхода:
  // человек, который о жесте не знает, оказывается запертым. Жест от края система даёт и так,
  // вторым способом.
  const кСписку = () => {
    navigate(
      { pathname: location.pathname.replace(/\/r\/[^/]+$/, ''), search: location.search },
      { replace: true },
    );
  };

  return (
    <section className='flex h-full min-w-0 flex-1 flex-col bg-bg-weak-50 узко-безстроки:hidden'>
      {/* Только в узком окне: на компьютере список и карточка стоят рядом, и возвращаться
          неоткуда. */}
      <button
        type='button'
        onClick={кСписку}
        data-печать='скрыть'
        className='hidden items-center gap-1 border-b border-stroke-soft-200 bg-bg-white-0 px-3 py-3 text-left text-label-sm text-text-sub-600 transition duration-200 ease-out hover:text-text-strong-950 узко-строка:flex'
      >
        <RiArrowLeftSLine className='size-5 shrink-0' />
        К списку
      </button>
      {/* Поле 16 в узком окне: там поверхность одна на весь экран, и список, карточка и
          панель обязаны начинать содержимое на ОДНОЙ вертикали — иначе при переходе между
          ними текст дёргается вбок. Собственные 20 правильны там, где карточка стоит
          отдельной колонкой рядом со списком. */}
      {hasHeader && (
        <header className='flex flex-col gap-2 border-b border-stroke-soft-200 bg-bg-white-0 px-5 py-3 узко:px-4'>
          {/* На узком экране заголовок и действия идут В СТОЛБИК, а не в ряд. В ряду заголовок
              стоит с `truncate` и уступает кнопкам: на карточке подотчётного лица их четыре, и
              при 672 точках ширины имени человека оставалось ноль — карточка открывалась БЕЗ
              ИМЕНИ. На компьютере это не видно, потому что рядом стоит список с выделенной
              строкой; в узком списка нет вовсе, и человек не знает, чью карточку читает. */}
          {(title !== undefined || actions !== undefined) && (
            <div className='flex items-center justify-between gap-3 тесно:flex-col тесно:items-start тесно:gap-2'>
              <h2 className='min-w-0 truncate text-label-md text-text-strong-950 тесно:w-full'>
                {title}
              </h2>
              {actions !== undefined && (
                // Кнопки над объектом на бумагу не идут: печатают документ, а не экран.
                // Заголовок при этом остаётся — имя человека на листе к месту.
                <div
                  data-печать='скрыть'
                  className='flex shrink-0 items-center gap-2 тесно:w-full тесно:flex-wrap'
                >
                  {actions}
                </div>
              )}
            </div>
          )}
          {/* Полоса вкладок — навигация, а не содержимое документа. */}
          {tabs !== undefined && <div data-печать='скрыть'>{tabs}</div>}
        </header>
      )}
      <div className='min-h-0 flex-1 overflow-y-auto'>{children}</div>
    </section>
  );
}
